import React, { useState } from 'react';
import { GameMode, AIDifficulty } from '../types';
import { WebSocketService } from '../services/WebSocketService';
import './UniversalGameModeSelector.css';

// --- Public Types ---

export interface DifficultyOption {
  id: string;
  label: string;
  icon: string;
  description?: string;
}

export interface ExtraOption {
  id: string;
  label: string;
  icon: string;
  description?: string;
}

export interface ExtraSection {
  /** Section title shown in the collapsible header */
  title: string;
  /** Icon shown next to the section title */
  icon: string;
  /** Options to display as pills */
  options: ExtraOption[];
  /** Which option is selected by default (id) */
  defaultSelected?: string;
  /** Whether this section is open by default */
  defaultOpen?: boolean;
  /** Which modes this section is visible for. Omit to show for all modes. */
  visibleForModes?: ('ai' | 'local' | 'online')[];
}

export interface GameModeConfig {
  /** Game display name */
  gameName: string;
  /** Game icon (emoji) */
  gameIcon: string;
  /** Subtitle text below the game name */
  subtitle?: string;
  /** Which modes to show. Defaults to all three. */
  availableModes?: ('ai' | 'local' | 'online')[];
  /** Custom difficulty options. Defaults to Easy/Medium/Hard. */
  difficulties?: DifficultyOption[];
  /** Extra collapsible sections (e.g., time control, board size) */
  extraSections?: ExtraSection[];
  /** Whether online multiplayer is available (checks WebSocket) */
  onlineEnabled?: boolean;
}

export interface GameModeSelection {
  mode: 'ai' | 'local' | 'online';
  difficulty?: string;
  extras?: Record<string, string>;
  onlineAction?: 'create' | 'join';
  roomCode?: string;
}

export interface UniversalGameModeSelectorProps {
  /** Game configuration */
  config: GameModeConfig;
  /** Called when the user starts a game */
  onStart: (selection: GameModeSelection) => void;
  /** Called when the user joins a room with a code */
  onJoinRoom?: (roomCode: string) => void;
  /** Called when the user creates a room */
  onCreateRoom?: () => void;
  /** Whether the component is in a loading state */
  isLoading?: boolean;
  /** Number of guest games remaining (shown as banner) */
  guestGamesRemaining?: number;
  /** Whether the user is authenticated */
  isAuthenticated?: boolean;
}

// --- Default Difficulties ---

const DEFAULT_DIFFICULTIES: DifficultyOption[] = [
  { id: AIDifficulty.EASY, label: 'Easy', icon: '☀️', description: 'AI makes mistakes' },
  { id: AIDifficulty.MEDIUM, label: 'Medium', icon: '⭐', description: 'Balanced gameplay' },
  { id: AIDifficulty.HARD, label: 'Hard', icon: '🏆', description: 'Unbeatable AI' },
];

// --- Component ---

const UniversalGameModeSelector: React.FC<UniversalGameModeSelectorProps> = ({
  config,
  onStart,
  onJoinRoom,
  onCreateRoom,
  isLoading = false,
  guestGamesRemaining,
  isAuthenticated = true,
}) => {
  const {
    gameName,
    gameIcon,
    subtitle = 'Choose how you want to play',
    availableModes = ['ai', 'local', 'online'],
    difficulties = DEFAULT_DIFFICULTIES,
    extraSections = [],
    onlineEnabled,
  } = config;

  // Determine if online is actually available
  const isOnlineAvailable = onlineEnabled !== undefined
    ? onlineEnabled
    : WebSocketService.isAvailable();

  const [selectedMode, setSelectedMode] = useState<'ai' | 'local' | 'online'>(availableModes[0]);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>(
    difficulties.length > 1 ? difficulties[1].id : difficulties[0]?.id || AIDifficulty.MEDIUM
  );
  const [extraSelections, setExtraSelections] = useState<Record<string, string>>(() => {
    const defaults: Record<string, string> = {};
    extraSections.forEach((section, idx) => {
      const key = `extra-${idx}`;
      defaults[key] = section.defaultSelected || section.options[0]?.id || '';
    });
    return defaults;
  });
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
    const defaults: Record<string, boolean> = {};
    defaults['difficulty'] = true;
    defaults['online'] = true;
    extraSections.forEach((section, idx) => {
      defaults[`extra-${idx}`] = section.defaultOpen ?? false;
    });
    return defaults;
  });
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [roomCode, setRoomCode] = useState('');
  const [roomCodeError, setRoomCodeError] = useState('');

  // --- Handlers ---

  const handleModeSelect = (mode: 'ai' | 'local' | 'online') => {
    setSelectedMode(mode);
    setShowJoinForm(false);
    setRoomCode('');
    setRoomCodeError('');
  };

  const handleDifficultySelect = (id: string) => {
    setSelectedDifficulty(id);
  };

  const handleExtraSelect = (sectionKey: string, optionId: string) => {
    setExtraSelections(prev => ({ ...prev, [sectionKey]: optionId }));
  };

  const toggleSection = (key: string) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleRoomCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (value.length <= 6) {
      setRoomCode(value);
      setRoomCodeError('');
    }
  };

  const handleJoinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (roomCode.length !== 6) {
      setRoomCodeError('Room code must be 6 characters');
      return;
    }
    if (onJoinRoom) {
      onJoinRoom(roomCode);
    }
  };

  const handleCreateRoom = () => {
    if (onCreateRoom) {
      onCreateRoom();
    } else {
      onStart({
        mode: 'online',
        onlineAction: 'create',
        extras: extraSelections,
      });
    }
  };

  const handleStart = () => {
    const selection: GameModeSelection = {
      mode: selectedMode,
      extras: extraSelections,
    };

    if (selectedMode === 'ai') {
      selection.difficulty = selectedDifficulty;
    }

    onStart(selection);
  };

  // --- Render Helpers ---

  const getStartButtonText = (): string => {
    if (selectedMode === 'ai') {
      const diff = difficulties.find(d => d.id === selectedDifficulty);
      return `▶ Start Game vs AI (${diff?.label || 'Medium'})`;
    }
    if (selectedMode === 'local') {
      return '▶ Start Local Game';
    }
    return '▶ Create Private Room';
  };

  const getStartButtonClass = (): string => {
    const base = 'ugms-start-btn';
    if (selectedMode === 'online') return `${base} ugms-start-btn--online`;
    if (selectedMode === 'local') return `${base} ugms-start-btn--local`;
    return base;
  };

  const getVisibleExtraSections = (): { section: ExtraSection; key: string }[] => {
    return extraSections
      .map((section, idx) => ({ section, key: `extra-${idx}` }))
      .filter(({ section }) => {
        if (!section.visibleForModes) return true;
        return section.visibleForModes.includes(selectedMode);
      });
  };

  // --- Render ---

  return (
    <div className="ugms-container" role="region" aria-label={`${gameName} game mode selector`}>
      {/* Header */}
      <div className="ugms-header">
        <div className="ugms-header__icon" aria-hidden="true">{gameIcon}</div>
        <h2 className="ugms-header__title">{gameName}</h2>
        <p className="ugms-header__subtitle">{subtitle}</p>
      </div>

      {/* Guest Banner */}
      {!isAuthenticated && guestGamesRemaining !== undefined && guestGamesRemaining > 0 && (
        <div className="ugms-guest-banner" role="alert">
          <span className="ugms-guest-banner__icon" aria-hidden="true">⚡</span>
          <span>{guestGamesRemaining} free game{guestGamesRemaining !== 1 ? 's' : ''} remaining</span>
        </div>
      )}

      {/* Mode Cards */}
      <div className="ugms-mode-cards" role="radiogroup" aria-label="Game mode">
        {availableModes.includes('ai') && (
          <button
            className={`ugms-mode-card ${selectedMode === 'ai' ? 'ugms-mode-card--selected' : ''}`}
            data-mode="ai"
            onClick={() => handleModeSelect('ai')}
            disabled={isLoading}
            role="radio"
            aria-checked={selectedMode === 'ai'}
            aria-label="Play vs AI"
          >
            <div className="ugms-mode-card__icon" aria-hidden="true">🧠</div>
            <div className="ugms-mode-card__label">vs AI</div>
            <div className="ugms-mode-card__desc">Play the computer</div>
          </button>
        )}

        {availableModes.includes('local') && (
          <button
            className={`ugms-mode-card ${selectedMode === 'local' ? 'ugms-mode-card--selected' : ''}`}
            data-mode="local"
            onClick={() => handleModeSelect('local')}
            disabled={isLoading}
            role="radio"
            aria-checked={selectedMode === 'local'}
            aria-label="Local multiplayer"
          >
            <div className="ugms-mode-card__icon" aria-hidden="true">🤝</div>
            <div className="ugms-mode-card__label">Local</div>
            <div className="ugms-mode-card__desc">Same device</div>
          </button>
        )}

        {availableModes.includes('online') && (
          <button
            className={`ugms-mode-card ${selectedMode === 'online' ? 'ugms-mode-card--selected' : ''}`}
            data-mode="online"
            onClick={() => handleModeSelect('online')}
            disabled={isLoading || !isOnlineAvailable}
            role="radio"
            aria-checked={selectedMode === 'online'}
            aria-label="Online multiplayer"
            title={!isOnlineAvailable ? 'Online multiplayer not available' : undefined}
          >
            <div className="ugms-mode-card__icon" aria-hidden="true">📡</div>
            <div className="ugms-mode-card__label">Online</div>
            <div className="ugms-mode-card__desc">
              {isOnlineAvailable ? 'Play friends' : 'Coming soon'}
            </div>
          </button>
        )}
      </div>

      {/* AI Difficulty Section */}
      {selectedMode === 'ai' && difficulties.length > 0 && (
        <div className={`ugms-section ${openSections['difficulty'] ? 'ugms-section--open' : ''}`}>
          <button
            className="ugms-section__header"
            onClick={() => toggleSection('difficulty')}
            aria-expanded={openSections['difficulty']}
            aria-controls="ugms-difficulty-body"
          >
            <div className="ugms-section__header-left">
              <span className="ugms-section__header-icon" aria-hidden="true">⚙️</span>
              <span className="ugms-section__header-title">Difficulty</span>
            </div>
            <span className="ugms-section__chevron" aria-hidden="true">▼</span>
          </button>
          <div className="ugms-section__body" id="ugms-difficulty-body" role="radiogroup" aria-label="Difficulty level">
            <div className="ugms-pills">
              {difficulties.map(diff => (
                <button
                  key={diff.id}
                  className={`ugms-pill ${selectedDifficulty === diff.id ? 'ugms-pill--selected' : ''}`}
                  onClick={() => handleDifficultySelect(diff.id)}
                  disabled={isLoading}
                  role="radio"
                  aria-checked={selectedDifficulty === diff.id}
                  aria-label={diff.label}
                >
                  <span className="ugms-pill__icon" aria-hidden="true">{diff.icon}</span>
                  {diff.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Online Section */}
      {selectedMode === 'online' && (
        <div className={`ugms-section ${openSections['online'] ? 'ugms-section--open' : ''}`}>
          <button
            className="ugms-section__header"
            onClick={() => toggleSection('online')}
            aria-expanded={openSections['online']}
            aria-controls="ugms-online-body"
          >
            <div className="ugms-section__header-left">
              <span className="ugms-section__header-icon" aria-hidden="true">🌐</span>
              <span className="ugms-section__header-title">Online Options</span>
            </div>
            <span className="ugms-section__chevron" aria-hidden="true">▼</span>
          </button>
          <div className="ugms-section__body" id="ugms-online-body">
            <div className="ugms-online-actions">
              <button
                className="ugms-online-btn"
                onClick={handleCreateRoom}
                disabled={isLoading}
              >
                <span className="ugms-online-btn__icon" aria-hidden="true">➕</span>
                <div className="ugms-online-btn__text">
                  <div className="ugms-online-btn__title">Create Room</div>
                  <div className="ugms-online-btn__desc">Start a new game and invite a friend</div>
                </div>
                <span className="ugms-online-btn__arrow" aria-hidden="true">→</span>
              </button>

              <button
                className="ugms-online-btn"
                onClick={() => setShowJoinForm(!showJoinForm)}
                disabled={isLoading}
              >
                <span className="ugms-online-btn__icon" aria-hidden="true">🔑</span>
                <div className="ugms-online-btn__text">
                  <div className="ugms-online-btn__title">Join Room</div>
                  <div className="ugms-online-btn__desc">Enter a room code to join</div>
                </div>
                <span className="ugms-online-btn__arrow" aria-hidden="true">→</span>
              </button>
            </div>

            {showJoinForm && (
              <form className="ugms-join-form" onSubmit={handleJoinSubmit}>
                <div className="ugms-join-form__row">
                  <input
                    className={`ugms-join-form__input ${roomCodeError ? 'ugms-join-form__input--error' : ''}`}
                    value={roomCode}
                    onChange={handleRoomCodeChange}
                    placeholder="ABC123"
                    maxLength={6}
                    disabled={isLoading}
                    aria-label="Room code"
                    aria-invalid={!!roomCodeError}
                    aria-describedby={roomCodeError ? 'ugms-room-code-error' : undefined}
                  />
                  <button
                    type="submit"
                    className="ugms-join-form__btn"
                    disabled={isLoading || roomCode.length !== 6}
                  >
                    Join
                  </button>
                </div>
                {roomCodeError && (
                  <div className="ugms-error-message" id="ugms-room-code-error" role="alert">
                    {roomCodeError}
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      )}

      {/* Extra Sections (game-specific, e.g., time control, board size) */}
      {getVisibleExtraSections().map(({ section, key }) => (
        <div
          key={key}
          className={`ugms-section ${openSections[key] ? 'ugms-section--open' : ''}`}
        >
          <button
            className="ugms-section__header"
            onClick={() => toggleSection(key)}
            aria-expanded={openSections[key]}
            aria-controls={`ugms-${key}-body`}
          >
            <div className="ugms-section__header-left">
              <span className="ugms-section__header-icon" aria-hidden="true">{section.icon}</span>
              <span className="ugms-section__header-title">{section.title}</span>
            </div>
            <span className="ugms-section__chevron" aria-hidden="true">▼</span>
          </button>
          <div className="ugms-section__body" id={`ugms-${key}-body`} role="radiogroup" aria-label={section.title}>
            <div className="ugms-pills">
              {section.options.map(option => (
                <button
                  key={option.id}
                  className={`ugms-pill ${extraSelections[key] === option.id ? 'ugms-pill--selected' : ''}`}
                  onClick={() => handleExtraSelect(key, option.id)}
                  disabled={isLoading}
                  role="radio"
                  aria-checked={extraSelections[key] === option.id}
                  aria-label={option.label}
                >
                  <span className="ugms-pill__icon" aria-hidden="true">{option.icon}</span>
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* Start Button (not shown for online mode — use Create/Join instead) */}
      {selectedMode !== 'online' && (
        <button
          className={getStartButtonClass()}
          onClick={handleStart}
          disabled={isLoading}
        >
          {getStartButtonText()}
        </button>
      )}
    </div>
  );
};

export default UniversalGameModeSelector;
